import React, { useEffect, useRef, useState } from "react";
import { Topbar } from "@/components/layout/Topbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
    UploadCloud, RefreshCcw, FileText, XCircle,
    Loader2, Zap, ChevronRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/rikkus/StatusBadge";
import { TimestampET } from "@/components/rikkus/TimestampET";

const STATUS_MAP = {
    PENDING:   { kind: "pending",  label: "PENDIENTE" },
    PARSING:   { kind: "pending",  label: "PARSEANDO" },
    PARSED:    { kind: "hit",      label: "PARSEADO" },
    PARTIAL:   { kind: "partial",  label: "PARCIAL" },
    FAILED:    { kind: "miss",     label: "FALLIDO" },
    DUPLICATE: { kind: "neutral",  label: "DUPLICADO" },
};

function bytesToHuman(n) {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const ACCEPTED_EXT = /\.(zip|drf)$/i;

export default function BrisPpPage() {
    const [uploads, setUploads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [error, setError] = useState(null);
    const [sweeping, setSweeping] = useState(false);
    const [sweepReport, setSweepReport] = useState(null);
    const [expanded, setExpanded] = useState({});
    const fileInputRef = useRef(null);

    const load = () => {
        setLoading(true);
        setError(null);
        api.get("/bris-pp-uploads?limit=100")
            .then((r) => setUploads(r.data || []))
            .catch((e) => setError(e?.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        const t = setInterval(() => {
            const inflight = uploads.some((u) => u.status === "PARSING" || u.status === "PENDING");
            if (inflight) load();
        }, 4000);
        return () => clearInterval(t);
    }, [uploads.some((u) => u.status === "PARSING" || u.status === "PENDING")]);

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length === 0) return;
        const bad = files.find((f) => !ACCEPTED_EXT.test(f.name));
        if (bad) {
            setError(`Solo se aceptan .zip o .DRF (rechazado: ${bad.name})`);
            return;
        }
        setUploading(true);
        setError(null);
        try {
            const formData = new FormData();
            files.forEach((f) => formData.append("files", f));
            await api.post("/bris-pp-uploads", formData, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 600000, // 10 min: PP files pueden pesar
            });
            load();
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setUploading(false);
        }
    };

    const runSweep = async () => {
        setSweeping(true);
        setError(null);
        try {
            const r = await api.post("/bris-pp-uploads/sweep");
            setSweepReport(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally {
            setSweeping(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        handleFiles(e.dataTransfer.files);
    };

    return (
        <>
            <Topbar
                title="PP Data Files — BRIS Single File"
                subtitle="Carga administrativa de BRIS Past Performances (Motor Step 2a)"
            />
            <div className="p-3 md:p-4 space-y-4 flex-1 overflow-auto">
                {/* Zona de carga drag-and-drop */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                            <UploadCloud className="h-4 w-4" /> Subir PP Data Files (.DRF)
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div
                            data-testid="bris-pp-dropzone"
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={
                                "border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors " +
                                (dragOver
                                    ? "border-primary bg-primary/5"
                                    : "border-border hover:border-primary/40 hover:bg-muted/20")
                            }
                        >
                            <input
                                data-testid="bris-pp-file-input"
                                ref={fileInputRef}
                                type="file"
                                accept=".zip,.DRF,.drf"
                                multiple
                                className="hidden"
                                onChange={(e) => handleFiles(e.target.files)}
                            />
                            {uploading ? (
                                <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                                    <Loader2 className="h-6 w-6 animate-spin" />
                                    Subiendo…
                                </div>
                            ) : (
                                <>
                                    <FileText className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                                    <div className="text-sm font-medium">
                                        Arrastrá acá los <code>.DRF</code> o los <code>.zip</code> (día-pista o ZIP-de-ZIPs) — o hacé click
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-1">
                                        Múltiples archivos por request. Máx 500 MB. Idempotente por SHA-256 del .DRF.
                                    </div>
                                </>
                            )}
                        </div>
                        {error && (
                            <div
                                data-testid="bris-pp-error-banner"
                                className="mt-3 text-xs text-danger font-mono flex items-center gap-2"
                            >
                                <XCircle className="h-4 w-4" /> {error}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Tabla de uploads */}
                <Card>
                    <CardHeader className="pb-3 flex flex-row items-center justify-between">
                        <CardTitle className="text-sm">Historial de cargas</CardTitle>
                        <div className="flex gap-2">
                            <Button
                                data-testid="bris-pp-sweep-btn"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={runSweep}
                                disabled={sweeping}
                                title="Cuenta el estado de las raw_captures BRIS_PP_SINGLE (Step 2a: sólo status; parser en 2b)"
                            >
                                {sweeping
                                    ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                                    : <Zap className="h-3.5 w-3.5 mr-2" />} Sweep (contar estados)
                            </Button>
                            <Button
                                data-testid="bris-pp-refresh-btn"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={load}
                            >
                                <RefreshCcw className="h-3.5 w-3.5 mr-2" /> Refrescar
                            </Button>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {sweepReport && (
                            <div className="mb-3 text-[11px] font-mono px-3 py-2 rounded bg-muted/40 text-muted-foreground">
                                Sweep: scanned={sweepReport.scanned} · PENDING={sweepReport.pending} ·
                                PARSED={sweepReport.parsed} · PARTIAL={sweepReport.partial} · FAILED={sweepReport.failed}
                                {sweepReport.note && <div className="mt-1 italic">{sweepReport.note}</div>}
                            </div>
                        )}
                        {loading && <Skeleton className="h-40 w-full" />}
                        {!loading && uploads.length === 0 && (
                            <div className="py-16 text-center text-sm text-muted-foreground">
                                No hay uploads todavía.
                            </div>
                        )}
                        {!loading && uploads.length > 0 && (
                            <div className="rounded-md border border-border overflow-hidden">
                                <Table data-testid="bris-pp-uploads-table">
                                    <TableHeader>
                                        <TableRow className="bg-muted/40 hover:bg-muted/40">
                                            <TableHead className="h-8 text-[11px] w-6"></TableHead>
                                            <TableHead className="h-8 text-[11px]">Subido</TableHead>
                                            <TableHead className="h-8 text-[11px]">Archivo</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">Tamaño</TableHead>
                                            <TableHead className="h-8 text-[11px] text-right">.DRF</TableHead>
                                            <TableHead className="h-8 text-[11px]">Pistas</TableHead>
                                            <TableHead className="h-8 text-[11px]">Fechas</TableHead>
                                            <TableHead className="h-8 text-[11px]">Estado</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {uploads.map((u) => {
                                            const stat = STATUS_MAP[u.status] || { kind: "neutral", label: u.status };
                                            const isOpen = expanded[u.upload_id];
                                            return (
                                                <React.Fragment key={u.upload_id}>
                                                    <TableRow
                                                        data-testid={`bris-pp-upload-row-${u.upload_id}`}
                                                        className="h-9 cursor-pointer"
                                                        onClick={() =>
                                                            setExpanded((x) => ({ ...x, [u.upload_id]: !x[u.upload_id] }))
                                                        }
                                                    >
                                                        <TableCell>
                                                            <ChevronRight
                                                                className={"h-3.5 w-3.5 text-muted-foreground transition-transform " + (isOpen ? "rotate-90" : "")}
                                                            />
                                                        </TableCell>
                                                        <TableCell className="text-xs">
                                                            <TimestampET value={u.uploaded_at} />
                                                        </TableCell>
                                                        <TableCell className="font-mono text-xs">
                                                            {u.original_filename}
                                                            {u.is_wrapper_zip && <span className="ml-2 text-[10px] text-muted-foreground">(wrapper)</span>}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono text-xs">
                                                            {bytesToHuman(u.byte_size)}
                                                        </TableCell>
                                                        <TableCell className="text-right font-mono text-xs">
                                                            {u.inner_files_count}
                                                        </TableCell>
                                                        <TableCell className="text-xs font-mono">
                                                            {u.tracks_distinct?.length > 0
                                                                ? u.tracks_distinct.join(", ")
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell className="text-xs font-mono">
                                                            {u.dates_min
                                                                ? (u.dates_min === u.dates_max
                                                                    ? u.dates_min
                                                                    : `${u.dates_min} … ${u.dates_max}`)
                                                                : "—"}
                                                        </TableCell>
                                                        <TableCell>
                                                            <StatusBadge kind={stat.kind}>{stat.label}</StatusBadge>
                                                        </TableCell>
                                                    </TableRow>
                                                    {isOpen && (
                                                        <UploadDetailRow uploadId={u.upload_id} />
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="py-4 text-[11px] text-muted-foreground space-y-1">
                        <div><strong className="text-foreground">Reglas de ingesta (Step 2a — sólo carga raw):</strong></div>
                        <ul className="list-disc pl-4 space-y-0.5">
                            <li>Cada <code>.DRF</code> = una fila en <code>raw_captures</code> con <code>source_type=&apos;BRIS_PP_SINGLE&apos;</code>, payload en base64.</li>
                            <li>Idempotencia por <code>SHA-256(.DRF)</code>: reuploads devuelven el mismo <code>raw_capture_id</code>.</li>
                            <li>Aceptamos: ZIP-de-ZIPs, ZIP con múltiples .DRF, ZIP de día-pista, o <code>.DRF</code> suelto.</li>
                            <li>El nombre del archivo (ej. <code>sar07162026.DRF</code>) se parsea para poblar <code>bris_track_code</code> y <code>race_date</code>.</li>
                            <li>Todos los uploads quedan en <code>parse_status=&apos;PENDING&apos;</code>. El parser DRF (1435 columnas) viene en <b>Step 2b</b>.</li>
                        </ul>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}


function UploadDetailRow({ uploadId }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancel = false;
        setLoading(true);
        api.get(`/bris-pp-uploads/${uploadId}`)
            .then((r) => { if (!cancel) setDetail(r.data); })
            .catch((e) => { if (!cancel) setDetail({ error: e?.response?.data?.detail || e.message }); })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [uploadId]);

    return (
        <TableRow
            data-testid={`bris-pp-upload-detail-${uploadId}`}
            className="bg-muted/10 hover:bg-muted/10"
        >
            <TableCell colSpan={8} className="p-3">
                {loading && <Skeleton className="h-16 w-full" />}
                {!loading && detail && !detail.error && (
                    <div className="space-y-2">
                        <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                            .DRF internos ({detail.raw_captures?.length || 0})
                        </div>
                        <div className="rounded border border-border overflow-hidden">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/40 hover:bg-muted/40">
                                        <TableHead className="h-7 text-[10px]">raw_id</TableHead>
                                        <TableHead className="h-7 text-[10px]">Archivo</TableHead>
                                        <TableHead className="h-7 text-[10px]">BRIS</TableHead>
                                        <TableHead className="h-7 text-[10px]">Fecha</TableHead>
                                        <TableHead className="h-7 text-[10px] text-right">Tamaño</TableHead>
                                        <TableHead className="h-7 text-[10px]">SHA-256</TableHead>
                                        <TableHead className="h-7 text-[10px]">Estado</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(detail.raw_captures || []).map((rc) => (
                                        <TableRow key={rc.raw_capture_id} className="h-7">
                                            <TableCell className="font-mono text-[11px]">{rc.raw_capture_id}</TableCell>
                                            <TableCell className="font-mono text-[11px]">{rc.inner_filename}</TableCell>
                                            <TableCell className="font-mono text-[11px]">{rc.bris_track_code || "—"}</TableCell>
                                            <TableCell className="font-mono text-[11px]">{rc.race_date || "—"}</TableCell>
                                            <TableCell className="font-mono text-[11px] text-right">{bytesToHuman(rc.byte_size)}</TableCell>
                                            <TableCell className="font-mono text-[10px] text-muted-foreground">
                                                {rc.payload_hash?.slice(0, 12)}…
                                            </TableCell>
                                            <TableCell>
                                                <StatusBadge
                                                    kind={STATUS_MAP[rc.parse_status]?.kind || "neutral"}
                                                >
                                                    {rc.parse_status}
                                                </StatusBadge>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        {detail.error_summary && (
                            <div className="text-[10px] font-mono text-muted-foreground">
                                {detail.error_summary}
                            </div>
                        )}
                    </div>
                )}
                {!loading && detail?.error && (
                    <div className="text-xs font-mono text-danger">{detail.error}</div>
                )}
            </TableCell>
        </TableRow>
    );
}
